import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { InnoHubRefIcon } from "../shared/InnoHubRefLink";
import { toast } from "sonner";
import { FileText, Plus, Landmark, Loader2 } from "lucide-react";

const STATUS_LABELS = {
  identificada: { label: "Identificada", color: "bg-slate-200 text-slate-800 border border-slate-300 dark:bg-slate-700 dark:text-slate-100" },
  presentada: { label: "Presentada", color: "bg-blue-200 text-blue-900 border border-blue-400 dark:bg-blue-900 dark:text-blue-100" },
  en_evaluacion: { label: "En evaluación", color: "bg-amber-200 text-amber-900 border border-amber-400 dark:bg-amber-900 dark:text-amber-100" },
  concedida: { label: "Concedida", color: "bg-green-200 text-green-900 border border-green-500 dark:bg-green-900 dark:text-green-100" },
  denegada: { label: "Denegada", color: "bg-red-200 text-red-900 border border-red-400 dark:bg-red-900 dark:text-red-100" },
  cerrada: { label: "Cerrada", color: "bg-slate-300 text-slate-900 border border-slate-400 dark:bg-slate-600 dark:text-slate-100" },
};
const STATUS_ORDER = ["identificada", "presentada", "en_evaluacion", "concedida", "denegada", "cerrada"];

const money = (v) => (v ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : "—");

export default function GrantApplicationsSection() {
  const { token, user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = user?.role === "admin" || user?.role === "pm";

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getGrantApplications(token);
      setApps(data.items || []);
    } catch (e) {
      toast.error("Error al cargar solicitudes de subvención");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (app) => {
    setEditing({ ...app });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editing?.name) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.updateGrantApplication(token, editing.id, editing);
      } else {
        await api.createGrantApplication(token, editing);
      }
      toast.success("Solicitud guardada");
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="grant-applications-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="w-4 h-4" /> Solicitudes de Subvención (ficha estructurada)
        </CardTitle>
        {canEdit && (
          <Button size="sm" className="gap-1.5" data-testid="new-grant-application-btn" onClick={() => openEdit({ status: "identificada" })}>
            <Plus className="w-4 h-4" /> Nueva solicitud
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : apps.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="grant-applications-empty">Sin solicitudes registradas todavía.</p>
        ) : (
          apps.map((app) => (
            <div
              key={app.id}
              className="rounded-lg border p-4 space-y-2 cursor-pointer hover:border-primary/50 transition-colors"
              data-testid={`grant-application-card-${app.id}`}
              onClick={() => canEdit && openEdit(app)}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{app.name}</p>
                  <p className="text-xs text-muted-foreground">{[app.funder, app.country, app.program].filter(Boolean).join(" · ")}</p>
                </div>
                <Badge className={STATUS_LABELS[app.status]?.color} data-testid={`grant-application-status-${app.id}`}>
                  {STATUS_LABELS[app.status]?.label || app.status}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                {app.application_number && <span>Nº solicitud: {app.application_number}</span>}
                {app.submission_date && <span>Presentada: {app.submission_date}</span>}
                {app.resolution_date && <span>Resolución: {app.resolution_date}</span>}
                {app.amount_requested && <span>Solicitado: {money(app.amount_requested)}</span>}
                {app.amount_awarded && <span>Concedido: {money(app.amount_awarded)}</span>}
              </div>
              {app.notes && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{app.notes}</p>}
              {(app.related_document_codes || []).length > 0 && (
                <div className="flex items-center gap-1.5 pt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{app.related_document_codes.length} documentos</span>
                  <InnoHubRefIcon refs={app.refs} />
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="grant-application-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar solicitud" : "Nueva solicitud de subvención"}</DialogTitle>
            <DialogDescription>Estado y fechas de una convocatoria concreta (no los documentos sueltos).</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nombre *</Label>
                <Input data-testid="grant-app-name-input" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Organismo financiador</Label>
                  <Input value={editing.funder || ""} onChange={(e) => setEditing({ ...editing, funder: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>País</Label>
                  <Input value={editing.country || ""} onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="grant-app-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_LABELS[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Fecha de presentación</Label>
                  <Input type="date" value={editing.submission_date || ""} onChange={(e) => setEditing({ ...editing, submission_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha de resolución</Label>
                  <Input type="date" value={editing.resolution_date || ""} onChange={(e) => setEditing({ ...editing, resolution_date: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Importe solicitado (€)</Label>
                  <Input type="number" value={editing.amount_requested || ""} onChange={(e) => setEditing({ ...editing, amount_requested: parseFloat(e.target.value) || null })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Importe concedido (€)</Label>
                  <Input type="number" value={editing.amount_awarded || ""} onChange={(e) => setEditing({ ...editing, amount_awarded: parseFloat(e.target.value) || null })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Notas</Label>
                <Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="grant-app-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
